import { FaLinkedin } from "react-icons/fa";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ExternalLink,
  Gauge,
  Monitor,
  RotateCcw,
  ScrollText,
  ShieldAlert,
  UserRound,
} from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CountryFlag } from "@/components/CountryFlag";
import { BrowserIcon } from "@/components/icons/BrowserIcon";
import { OsIcon } from "@/components/icons/OsIcon";
import { formatBrowser, parseBrowser } from "@/lib/userAgent";
import { formatOs } from "@/lib/os";
import { useToast } from "@/hooks/use-toast";
import type { PluginStatus } from "@/services/pluginStatusApi";
import { resetLinkedinAccountQuotas, resetUserQuotas } from "@/services/quotaConfigApi";
import { listPluginLogs, type PluginErrorLog } from "@/services/pluginLogsApi";
import { ageSeconds, formatDuration, isWorkerErrorAlert, nextJobAuthLabel } from "./workerState";

// Timeline « Derniers logs » du drawer : page courte, le reste vit sur la
// page dédiée /plugin-logs (filtres + stack/contexte en détail).
const LOGS_PAGE_SIZE = 20;

const SEVERITY_DOT: Record<string, string> = {
  error: "bg-red-500",
  warning: "bg-amber-500",
  transient: "bg-muted-foreground/50",
};

// --- Formatters (locaux : le drawer reste autonome vs page.tsx) ------------- //
const fmtSecondsAgo = (diff: number | null): string => {
  if (diff === null) return "—";
  if (diff < 0) return "à l'instant";
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
};
const fmtAgo = (iso?: string | null): string => {
  if (!iso) return "—";
  return fmtSecondsAgo(Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
};
const fmtMsAgo = (ms?: number | null): string => (ms ? fmtAgo(new Date(ms).toISOString()) : "—");
// Plugin epoch ms -> ISO, for the local-clock fallback of `ageSeconds`.
const isoFromMs = (ms?: number | null): string | null => {
  if (!ms) return null;
  const at = new Date(ms);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
};
const fmtResetCountdown = (iso: string): string => {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "imminent";
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `dans ${h}h ${m % 60}m` : `dans ${m}m`;
};

// --- Barre de quota colorée (vert < 80 %, ambre < 100 %, rouge >= 100 %) ----- //
function QuotaBar({ label, used, cap }: { label: string; used: number | null; cap: number | null }) {
  const has = used != null && cap != null && cap > 0;
  const pct = has ? Math.min(100, Math.round((used! / cap!) * 100)) : 0;
  const color = !has
    ? "bg-muted-foreground/30"
    : pct >= 100
    ? "bg-red-500"
    : pct >= 80
    ? "bg-amber-500"
    : "bg-emerald-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">
          {used ?? "—"}
          <span className="text-muted-foreground"> / {cap ?? "—"}</span>
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-sm">{children}</span>
    </div>
  );
}

interface Props {
  plugin: PluginStatus | null;
  onClose: () => void;
  /** Appelé après un reset réussi pour rafraîchir la liste. */
  onResetDone: () => void;
}

export function PluginDetailSheet({ plugin, onClose, onResetDone }: Props) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [resetting, setResetting] = useState(false);
  // Reset par bucket sur l'axe abonnement (invitation | message) — distinct du
  // reset compte LinkedIn (`resetting`) qui vide scrape/search.
  const [resettingBucket, setResettingBucket] = useState<"invitation" | "message" | null>(null);
  // Historique persisté (plugin_error_logs) du worker affiché. Distinct du
  // bandeau `last_worker_error` (état LIVE du heartbeat, un seul slot).
  const [logs, setLogs] = useState<PluginErrorLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  const aq = plugin?.account_quotas ?? null;
  const tier = aq?.tier ?? plugin?.tier ?? "new";
  const workerId = plugin?.worker_id ?? null;
  const errorAlert = plugin ? isWorkerErrorAlert(plugin) : false;

  // (Re)charge la première page de logs à l'ouverture / au changement de
  // worker. Best-effort : un échec laisse la section vide sans casser le
  // drawer. Pas de refresh 10s ici — l'historique bouge lentement et le
  // drawer suit déjà le refresh de la page pour l'état live.
  useEffect(() => {
    if (!workerId) {
      setLogs([]);
      setLogsTotal(0);
      return;
    }
    let cancelled = false;
    setLogsLoading(true);
    listPluginLogs(navigate, { worker_id: workerId, limit: LOGS_PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setLogs(res.items);
        setLogsTotal(res.total);
      })
      .catch(() => {
        if (cancelled) return;
        setLogs([]);
        setLogsTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const loadMoreLogs = async () => {
    if (!workerId || logsLoading) return;
    setLogsLoading(true);
    try {
      const res = await listPluginLogs(navigate, {
        worker_id: workerId,
        limit: LOGS_PAGE_SIZE,
        offset: logs.length,
      });
      setLogs((prev) => [...prev, ...res.items]);
      setLogsTotal(res.total);
    } catch {
      // best-effort — le bouton reste cliquable pour retenter
    } finally {
      setLogsLoading(false);
    }
  };

  const handleReset = async () => {
    const pid = aq?.public_identifier;
    if (!pid) return;
    setResetting(true);
    try {
      const res = await resetLinkedinAccountQuotas(pid, null, navigate);
      toast({
        title: "Compteurs réinitialisés",
        description: `${res.flushed_keys} clé(s) Redis vidée(s) pour ${pid}.`,
      });
      onResetDone();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Échec du reset",
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };

  // Reset ciblé d'un bucket (invitation | message) sur l'axe ABONNEMENT
  // (`quota:{keycloak_sub}:{bucket}:{h,d}`) — c'est le cap "max par heure/jour"
  // réellement appliqué aux envois. Ne touche pas le quota par-campagne ni le
  // mensuel org. plugin.user_id == keycloak sub == argument de resetUserQuotas.
  const handleResetBucket = async (bucket: "invitation" | "message") => {
    const uid = plugin?.user_id;
    if (!uid) return;
    setResettingBucket(bucket);
    try {
      const res = await resetUserQuotas(uid, [bucket], navigate);
      toast({
        title: "Quota réinitialisé",
        description: `${res.flushed_keys} clé(s) Redis vidée(s) — ${
          bucket === "message" ? "messages" : "invitations"
        } (1 h + 24 h).`,
      });
      onResetDone();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Échec du reset",
        variant: "destructive",
      });
    } finally {
      setResettingBucket(null);
    }
  };

  return (
    <Sheet open={!!plugin} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {plugin && (
          <>
            <SheetHeader>
              <SheetTitle className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{plugin.worker_id.substring(0, 12)}…</span>
                {plugin.is_anonymous ? (
                  <Badge variant="secondary">Anonyme</Badge>
                ) : (
                  <Badge variant="default">{plugin.user_name || "Authentifié"}</Badge>
                )}
                <Badge variant={tier === "trusted" ? "default" : "outline"}>{tier}</Badge>
                {plugin.linkedin_checkpoint?.active && (
                  <Badge variant="outline" className="gap-1 text-amber-600">
                    <ShieldAlert size={11} /> Vérif. LinkedIn
                  </Badge>
                )}
              </SheetTitle>
              <SheetDescription>
                Détail du plugin connecté — worker, compte LinkedIn, compte RecrutAuto et quotas.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-5">
              {/* --- Worker --- */}
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <Monitor size={15} /> Worker
                </h3>
                <div className="space-y-1.5">
                  <Row label="Worker ID">
                    <code className="break-all text-xs">{plugin.worker_id}</code>
                  </Row>
                  <Row label="IP">{plugin.ip || "—"}</Row>
                  <Row label="Localisation">
                    {plugin.location ? (
                      <span className="inline-flex items-center gap-1.5">
                        <CountryFlag location={plugin.location} />
                        {[plugin.location.city, plugin.location.country].filter(Boolean).join(", ")}
                        {plugin.location.isp ? ` · ${plugin.location.isp}` : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </Row>
                  <Row label="Navigateur">
                    <span className="inline-flex items-center gap-1.5">
                      <BrowserIcon name={parseBrowser(plugin.user_agent)?.name || plugin.browser_name} />
                      {formatBrowser(plugin.user_agent, plugin.browser_name, plugin.browser_version)}
                    </span>
                  </Row>
                  <Row label="OS">
                    <span className="inline-flex items-center gap-1.5">
                      <OsIcon name={plugin.os_name} />
                      {formatOs(plugin.os_name, plugin.os_version)}
                    </span>
                  </Row>
                  <Row label="Version plugin">
                    {plugin.plugin_version ? <Badge variant="outline">{plugin.plugin_version}</Badge> : "—"}
                  </Row>
                  {/* Ages: server clock (backoffice), never this browser's. */}
                  <Row label="Heartbeat">
                    {fmtSecondsAgo(ageSeconds(plugin.last_seen_age_s, plugin.last_seen))}
                  </Row>
                  <Row label="Dernier next-job">
                    {plugin.last_next_job_call
                      ? fmtSecondsAgo(ageSeconds(plugin.activity_age_s, plugin.last_next_job_call))
                      : "aucun (inactif)"}
                  </Row>
                  <Row label="Dernier job">
                    <code className="text-xs">{plugin.last_job_type ?? "—"}</code>
                    {plugin.blocked_bucket ? ` (bucket ${plugin.blocked_bucket})` : ""}
                  </Row>
                  {plugin.job_running_age_s != null && (
                    // Plan 5.4: no poll happens during a job (busy, not stalled).
                    <Row label="Job en cours">depuis {formatDuration(plugin.job_running_age_s)}</Row>
                  )}
                  <Row label="Authentifié (next-job)">{nextJobAuthLabel(plugin)}</Row>
                  <Row label="Alarmes (heartbeat / job)">
                    {fmtMsAgo(plugin.last_alarm_fired_at?.heartbeat)} / {fmtMsAgo(plugin.last_alarm_fired_at?.job_check)}
                    {plugin.job_check_period_minutes != null ? ` · période ${plugin.job_check_period_minutes}m` : ""}
                  </Row>
                  <Row label="Échecs job consécutifs">
                    {plugin.consecutive_job_failures ?? "—"}
                  </Row>
                </div>
                {plugin.last_worker_error && (
                  // Plan 5.1: red + ⚠ only for an error younger than 30 min and not
                  // transient (backoffice verdict); otherwise shown in neutral grey.
                  <div
                    className={`mt-2 space-y-1 rounded-md border p-2 text-xs ${
                      errorAlert
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {errorAlert ? <AlertTriangle size={14} className="mt-0.5 shrink-0" /> : null}
                      <div className="min-w-0">
                        <code>
                          {plugin.last_worker_error.source}/{plugin.last_worker_error.reason}
                        </code>
                        {plugin.last_worker_error.severity &&
                        plugin.last_worker_error.severity !== "error" ? (
                          <span className="ml-1 opacity-70">
                            ({plugin.last_worker_error.severity})
                          </span>
                        ) : null}
                        {plugin.last_worker_error.message ? (
                          <div className="break-all opacity-80">{plugin.last_worker_error.message}</div>
                        ) : null}
                        <div className="opacity-70">
                          {fmtSecondsAgo(
                            ageSeconds(plugin.last_worker_error_age_s, isoFromMs(plugin.last_worker_error.ts)),
                          )}
                          {plugin.last_worker_error.consecutive_failures != null
                            ? ` · ${plugin.last_worker_error.consecutive_failures} échec(s) consécutif(s)`
                            : ""}
                          {errorAlert ? "" : " · pas d'alerte (transitoire ou de plus de 30 min)"}
                        </div>
                      </div>
                    </div>
                    {plugin.last_worker_error.stack ? (
                      <pre
                        className={`mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded p-1.5 text-[11px] leading-snug ${errorAlert ? "bg-red-100/60" : "bg-slate-100"}`}
                      >
                        {plugin.last_worker_error.stack}
                      </pre>
                    ) : null}
                    {plugin.last_worker_error.context &&
                    Object.keys(plugin.last_worker_error.context).length > 0 ? (
                      <pre
                        className={`mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded p-1.5 text-[11px] leading-snug ${errorAlert ? "bg-red-100/60" : "bg-slate-100"}`}
                      >
                        {JSON.stringify(plugin.last_worker_error.context, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                )}
                {plugin.linkedin_checkpoint?.active && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                    Vérification LinkedIn requise
                    {plugin.linkedin_checkpoint.type ? ` · ${plugin.linkedin_checkpoint.type}` : ""}
                    {plugin.linkedin_checkpoint.detected_at
                      ? ` · en pause depuis ${fmtMsAgo(plugin.linkedin_checkpoint.detected_at)}`
                      : ""}
                  </div>
                )}
              </section>

              <Separator />

              {/* --- Derniers logs (historique persisté plugin_error_logs) ---
                  Le bandeau rouge ci-dessus = dernier état LIVE (heartbeat,
                  un slot). Ici : l'historique durable, y compris les chemins
                  silencieux v1.8 (onglet LinkedIn indisponible, erreurs
                  réseau /next-job, transitions auth/quota…). */}
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <ScrollText size={15} /> Derniers logs
                  {logsTotal > 0 && (
                    <span className="font-normal text-muted-foreground">({logsTotal})</span>
                  )}
                </h3>
                {logsLoading && logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Chargement…</p>
                ) : logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aucun log persisté — plugin antérieur à la télémétrie v1.8,
                    ou aucun événement depuis la rétention.
                  </p>
                ) : (
                  <>
                    <ul className="space-y-1.5">
                      {logs.map((log) => (
                        <li key={log.id} className="flex items-start gap-2 text-xs">
                          <span
                            className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                              SEVERITY_DOT[log.severity] ?? SEVERITY_DOT.error
                            }`}
                            title={log.severity}
                          />
                          <div className="min-w-0">
                            <code className="break-all">
                              {log.source}/{log.reason}
                            </code>
                            <span className="ml-1 text-muted-foreground">
                              {log.created_at ? fmtAgo(log.created_at) : "—"}
                            </span>
                            {log.message && (
                              <div className="line-clamp-2 break-all text-muted-foreground">
                                {log.message}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 flex items-center gap-2">
                      {logs.length < logsTotal && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={logsLoading}
                          onClick={loadMoreLogs}
                        >
                          {logsLoading ? "…" : `Charger plus (${logs.length}/${logsTotal})`}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/plugin-logs")}
                      >
                        Tous les logs
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  </>
                )}
              </section>

              <Separator />

              {/* --- Compte LinkedIn --- */}
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <FaLinkedin size={15} /> Compte LinkedIn
                </h3>
                {plugin.linkedin_user?.firstName || aq?.public_identifier ? (
                  <div className="space-y-1.5">
                    <Row label="Nom">
                      {[plugin.linkedin_user?.firstName, plugin.linkedin_user?.lastName]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </Row>
                    {plugin.linkedin_user?.occupation && (
                      <Row label="Poste">{plugin.linkedin_user.occupation}</Row>
                    )}
                    <Row label="Public ID">
                      {plugin.linkedin_user?.publicIdentifier ? (
                        <a
                          href={`https://www.linkedin.com/in/${plugin.linkedin_user.publicIdentifier}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-500 hover:underline"
                        >
                          {plugin.linkedin_user.publicIdentifier}
                          <ExternalLink size={11} />
                        </a>
                      ) : (
                        "—"
                      )}
                    </Row>
                    <Row label="Tier anti-détection">
                      <Badge variant={tier === "trusted" ? "default" : "secondary"}>{tier}</Badge>
                    </Row>
                    {aq && (
                      <Row label="Clé quota">
                        <code className="text-xs">{aq.account_key}</code>
                      </Row>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aucun compte LinkedIn détecté sur ce worker.
                  </p>
                )}
              </section>

              <Separator />

              {/* --- Compte RecrutAuto --- */}
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <UserRound size={15} /> Compte RecrutAuto
                </h3>
                {plugin.is_anonymous || !plugin.user_id ? (
                  <p className="text-sm text-muted-foreground">
                    Plugin anonyme — non rattaché à un compte RecrutAuto (pas de JWT).
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    <Row label="Utilisateur">{plugin.user_name || "—"}</Row>
                    <Row label="User ID">
                      <code className="break-all text-xs">{plugin.user_id}</code>
                    </Row>
                    {plugin.quotas && (
                      <>
                        <Row label="Invitations (jour, legacy)">
                          <span className="font-mono">
                            {plugin.quotas.invites.used}/{plugin.quotas.invites.limit}
                          </span>
                        </Row>
                        <Row label="Messages (jour, legacy)">
                          <span className="font-mono">
                            {plugin.quotas.messages.used}/{plugin.quotas.messages.limit}
                          </span>
                        </Row>
                        <Row label="Reset legacy">
                          {fmtResetCountdown(plugin.quotas.resets_at)}
                          {plugin.quotas.stale ? " · reset en attente" : ""}
                        </Row>
                      </>
                    )}

                    {/* Quota GLISSANT par abonnement (axe rolling) — c'est le cap
                        réellement appliqué aux envois. La barre 1 h rouge = cap
                        max/heure atteint → messages/invitations bloqués. */}
                    {plugin.subscription_quotas && (
                      <div className="mt-3 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium">Quota glissant (envois)</span>
                          {plugin.blocked_bucket === "message" && (
                            <Badge variant="outline" className="gap-1 text-red-600">
                              <ShieldAlert size={11} /> bloqué : messages
                            </Badge>
                          )}
                          {plugin.blocked_bucket === "invitation" && (
                            <Badge variant="outline" className="gap-1 text-red-600">
                              <ShieldAlert size={11} /> bloqué : invitations
                            </Badge>
                          )}
                        </div>
                        {(["message", "invitation"] as const).map((b) => {
                          const bk = plugin.subscription_quotas![b];
                          return (
                            <div key={b} className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">
                                  {b === "message" ? "Messages" : "Invitations"}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 gap-1 px-2 text-xs"
                                  onClick={() => handleResetBucket(b)}
                                  disabled={resettingBucket !== null}
                                >
                                  <RotateCcw size={12} />
                                  {resettingBucket === b ? "Reset…" : "Reset"}
                                </Button>
                              </div>
                              <QuotaBar label="24 h glissantes" used={bk.used_daily} cap={bk.cap_daily} />
                              <QuotaBar label="1 h glissante" used={bk.used_hourly} cap={bk.cap_hourly} />
                            </div>
                          );
                        })}
                        <p className="text-[11px] leading-snug text-muted-foreground">
                          Axe <code>rolling</code> par abonnement — cap réellement appliqué aux
                          envois. Le reset vide ce bucket (1 h + 24 h) uniquement ; il ne touche
                          ni le quota par-campagne ni le compteur mensuel org.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </section>

              <Separator />

              {/* --- Quotas anti-ban (par compte LinkedIn, account_rolling) --- */}
              <section>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <Gauge size={15} /> Quotas anti-ban (par compte LinkedIn)
                  </h3>
                  {aq && !aq.enforcement_enabled && (
                    <Badge variant="outline" className="gap-1 text-amber-600">
                      <ShieldAlert size={11} /> shadow
                    </Badge>
                  )}
                </div>
                {aq ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Axe <code>account_rolling</code> —{" "}
                      {aq.account_key.startsWith("li:")
                        ? "compte LinkedIn identifié"
                        : "worker anonyme (clé wk:, non encore rattaché à un compte)"}
                      .
                      {!aq.enforcement_enabled &&
                        " Enforcement désactivé (WORKER_ACCOUNT_QUOTA_ENABLED=false) : phase shadow — les compteurs se remplissent mais aucun worker n'est bloqué."}
                    </p>
                    {(["scrape", "search"] as const).map((b) => {
                      const bk = aq.buckets[b];
                      return (
                        <div key={b} className="space-y-1.5">
                          <div className="text-xs font-medium">
                            {b === "scrape"
                              ? "Scrape (enrichment / company / school / jobs / checks)"
                              : "Recherches LinkedIn"}
                          </div>
                          <QuotaBar label="24 h glissantes" used={bk.used_daily} cap={bk.cap_daily} />
                          <QuotaBar label="1 h glissante" used={bk.used_hourly} cap={bk.cap_hourly} />
                        </div>
                      );
                    })}
                    {aq.public_identifier && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                        disabled={resetting}
                        className="gap-1.5"
                      >
                        <RotateCcw size={14} /> {resetting ? "Reset…" : "Réinitialiser les compteurs"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Pas de clé de quota résolue pour ce worker.
                  </p>
                )}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
